/**
 * Bộ chạy thử luồng ngay trên trình duyệt (demo). Đi từ khối Nguồn theo cạnh,
 * mỗi khối ghi một bước (đầu vào/đầu ra) để UI tô màu và mở xem như n8n.
 * Không gửi gì thật: hành động chỉ sinh "đầu ra" (nội dung tin đã thay biến,
 * mã voucher, nhãn…) và biến cho khối sau (`{{voucher.code}}`).
 */
import type {
  ConditionRuleV2, FlowNodeV2, Mapping, MappingRule, RunStep, SampleEvent, Transform, WorkflowRun, WorkflowV2,
} from './types'
import { ACTION_V2_LABELS, genId } from './types'
import { tachBanGhiKiotViet } from './kiotviet'

const INTERNAL_OBJECT_TEN: Record<string, string> = { customer: 'Khách hàng', conversation: 'Hội thoại', order: 'Đơn hàng', task: 'Việc', appointment: 'Lịch hẹn' }

// ── Truy cập đường dẫn ────────────────────────────────────────────────
export function layGiaTri(obj: unknown, path: string): unknown {
  if (!path) return undefined
  let cur: unknown = obj
  for (const p of path.replace(/\[\]/g, '').split('.')) {
    if (cur == null) return undefined
    cur = (cur as Record<string, unknown>)[p]
  }
  return cur
}

function datGiaTri(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.')
  let cur: Record<string, unknown> = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    if (typeof cur[k] !== 'object' || cur[k] == null) cur[k] = {}
    cur = cur[k] as Record<string, unknown>
  }
  cur[parts[parts.length - 1]] = value
}

export function bienDoi(v: unknown, t: Transform): unknown {
  if (v == null) return v
  switch (t) {
    case 'trim': return String(v).trim()
    case 'upper': return String(v).toUpperCase()
    case 'phone_vn': {
      const so = String(v).replace(/\D/g, '')
      if (so.startsWith('84') && so.length >= 11) return '0' + so.slice(2)
      return so
    }
    case 'number': return typeof v === 'number' ? v : Number(String(v).replace(/[^\d.-]/g, '')) || 0
    case 'date': { const d = new Date(String(v)); return isNaN(d.getTime()) ? String(v) : d.toISOString() }
    default: return v
  }
}

/** Áp bảng ánh xạ lên một bản ghi nguồn → record CRM. Hỗ trợ mảng `A[].x → b.items[].y`. */
export function apAnhXa(banGhi: Record<string, unknown>, rules: MappingRule[]): Record<string, unknown> {
  const ra: Record<string, unknown> = {}
  for (const r of rules) {
    if (r.from.includes('[]') && r.to.includes('[]')) {
      const [mangNguon, truongNguon] = r.from.split('[].')
      const [mangDich, truongDich] = r.to.split('[].')
      const arr = layGiaTri(banGhi, mangNguon)
      if (!Array.isArray(arr)) continue
      const dich = (layGiaTri(ra, mangDich) as Record<string, unknown>[] | undefined) ?? []
      arr.forEach((it, i) => {
        dich[i] = dich[i] ?? {}
        dich[i][truongDich] = bienDoi((it as Record<string, unknown>)[truongNguon], r.transform)
      })
      datGiaTri(ra, mangDich, dich)
    } else {
      datGiaTri(ra, r.to, bienDoi(layGiaTri(banGhi, r.from), r.transform))
    }
  }
  return ra
}

// ── Điều kiện ────────────────────────────────────────────────────────
function soSanh(v: unknown, op: ConditionRuleV2['op'], value: string): boolean {
  const s = v == null ? '' : String(v).toLowerCase()
  // `value` có thể là số khi luồng do AI dựng hoặc nhập bằng JSON → ép chuỗi.
  const t = String(value ?? '').toLowerCase().trim()
  switch (op) {
    case 'eq': return s === t
    case 'neq': return s !== t
    case 'contains': return s.includes(t)
    case 'not_contains': return !s.includes(t)
    case 'in': return t.split(',').map((x) => x.trim()).filter(Boolean).some((x) => s === x || s.includes(x))
    case 'gte': return Number(v) >= Number(value)
    case 'lte': return Number(v) <= Number(value)
    case 'exists': return v != null && s !== ''
    case 'not_exists': return v == null || s === ''
  }
}

/** Trường dạng `order.items[].category` → ĐÚNG khi BẤT KỲ phần tử thoả. */
/** Biến thời gian theo giờ Việt Nam (khớp máy chủ): {{now.date}} · {{now.mmdd}} · {{now.hour}} */
export function mocThoiGian(d = new Date()): { date: string; mmdd: string; hour: number } {
  const vn = new Date(d.getTime() + 7 * 3_600_000)
  const date = vn.toISOString().slice(0, 10)
  return { date, mmdd: date.slice(5), hour: vn.getUTCHours() }
}

export function kiemTraLuat(ctx: Record<string, unknown>, luat: ConditionRuleV2): { dat: boolean; giaTri: unknown } {
  const r = typeof luat.value === 'string' && luat.value.includes('{{') ? { ...luat, value: thayBien(luat.value, { now: mocThoiGian(), ...ctx }) } : luat
  if (r.field.includes('[]')) {
    const [mang, truong] = r.field.split('[].')
    const arr = layGiaTri(ctx, mang)
    if (!Array.isArray(arr)) return { dat: r.op === 'not_exists', giaTri: undefined }
    const khop = arr.filter((it) => soSanh((it as Record<string, unknown>)[truong], r.op, r.value))
    return { dat: khop.length > 0, giaTri: khop.map((it) => (it as Record<string, unknown>)[truong]) }
  }
  const v = layGiaTri(ctx, r.field)
  return { dat: soSanh(v, r.op, r.value), giaTri: v }
}

// ── Thay biến {{a.b}} / {{order.items[0].name}} ───────────────────────
export function thayBien(mau: string, ctx: Record<string, unknown>): string {
  return mau.replace(/\{\{\s*([\w.[\]]+)\s*\}\}/g, (_m, path: string) => {
    const norm = path.replace(/\[(\d+)\]/g, '.$1')
    const v = layGiaTri(ctx, norm)
    if (v == null) return `⟨thiếu ${path}⟩`
    if (typeof v === 'number') return new Intl.NumberFormat('vi-VN').format(v)
    return String(v)
  })
}

// ── Chạy ─────────────────────────────────────────────────────────────
const DON_VI_CHO: Record<string, string> = { minutes: 'phút', hours: 'giờ', days: 'ngày' }
const MS_DON_VI: Record<string, number> = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 }

export function chayThu(wf: WorkflowV2, sample: SampleEvent, mappings: Mapping[]): WorkflowRun {
  const steps: RunStep[] = []
  const ctx: Record<string, unknown> = { source: { type: sample.source, event: sample.event }, raw: sample.payload }
  let status: WorkflowRun['status'] = 'ok'

  const nguon = wf.nodes.find((n) => n.kind === 'source')
  if (!nguon) {
    return { id: genId('run'), workflowId: wf.id, workflowName: wf.name, sampleId: sample.id, sampleLabel: sample.label, startedAt: new Date().toISOString(), status: 'fail', steps: [{ nodeId: '-', kind: 'source', label: 'Nguồn', status: 'fail', summary: 'Luồng chưa có khối Nguồn dữ liệu' }], context: ctx }
  }

  // Nguồn: 4 chế độ. Nội bộ / lịch / thủ công → payload đã ở dạng CRM, đổ thẳng
  // vào ngữ cảnh (không cần Ánh xạ). Ngoài → giữ bản ghi thô cho khối Ánh xạ.
  const mode = String(nguon.config.mode ?? 'external')
  let banGhi: Record<string, unknown> = (sample.payload as Record<string, unknown>) ?? {}
  let khopSuKien = true
  let lyDo = ''
  if (mode === 'external') {
    const suKienChon = String(nguon.config.event ?? '')
    if (sample.source === 'kiotviet') {
      const ds = tachBanGhiKiotViet(sample.payload)
      banGhi = ds[0]?.data ?? {}
      ctx.kiotviet = { action: ds[0]?.action, soBanGhi: ds.length }
    } else if (sample.source === 'gsheet') {
      banGhi = ((sample.payload as { values?: Record<string, unknown> }).values) ?? {}
    }
    khopSuKien = !['kiotviet', 'gsheet', 'webhook', 'fmrp'].includes(sample.source) ? false : (!suKienChon || suKienChon === sample.event)
    lyDo = khopSuKien ? `Nhận sự kiện ${sample.event} từ ${sample.source}` : `Bỏ qua: luồng chờ "${suKienChon}" từ nguồn ngoài, sự kiện tới là "${sample.event}" (${sample.source})`
  } else {
    Object.assign(ctx, banGhi)
    if (mode === 'internal') {
      const obj = String(nguon.config.object ?? ''); const ev = String(nguon.config.internalEvent ?? '')
      const field = String(nguon.config.field ?? '')
      // Dạng "customer.field_changed:customer.memberRank" — tách ở dấu chấm ĐẦU, rồi ':'.
      const dot = sample.event.indexOf('.')
      const evObj = dot > 0 ? sample.event.slice(0, dot) : sample.event
      const [evKind, evField] = (dot > 0 ? sample.event.slice(dot + 1) : '').split(':')
      khopSuKien = sample.source === 'chatmql' && evObj === obj && (!ev || ev === evKind) && (ev !== 'field_changed' || !field || field === evField)
      lyDo = khopSuKien ? `${INTERNAL_OBJECT_TEN[obj] ?? obj}: ${ev || 'sự kiện'}${field ? ` (${field})` : ''}` : `Bỏ qua: luồng chờ ${obj}.${ev}${field ? `:${field}` : ''}, sự kiện tới là ${sample.event}`
      // "Đổi thành giá trị" / lọc "chỉ khi"
      const toValue = String(nguon.config.toValue ?? '').trim()
      if (khopSuKien && ev === 'field_changed' && field && toValue) {
        const v = layGiaTri(ctx, field)
        if (String(v ?? '').toLowerCase() !== toValue.toLowerCase()) { khopSuKien = false; lyDo = `Bỏ qua: ${field} = "${v}" không phải "${toValue}"` }
      }
    } else if (mode === 'schedule') {
      khopSuKien = sample.source === 'schedule'
      lyDo = khopSuKien ? `Lịch ${nguon.config.cron || '(chưa đặt)'} chạy — bản ghi thoả điều kiện quét` : `Bỏ qua: luồng chạy theo lịch, sự kiện tới là ${sample.event}`
    } else if (mode === 'manual') {
      khopSuKien = sample.event.startsWith('manual.')
      lyDo = khopSuKien ? `Nhân viên bấm nút "${nguon.config.buttonLabel || 'Chạy luồng'}"` : `Bỏ qua: luồng chạy thủ công, sự kiện tới là ${sample.event}`
    }
    const rules = (nguon.config.filterRules as ConditionRuleV2[]) ?? []
    if (khopSuKien && rules.length) {
      const kq = rules.map((r) => kiemTraLuat(ctx, r))
      if (!kq.every((x) => x.dat)) { khopSuKien = false; lyDo = `Bỏ qua: không thoả lọc "chỉ khi" (${rules.map((r) => `${r.field} ${r.op} ${r.value}`).join(', ')})` }
    }
  }
  ctx.record = banGhi
  steps.push({
    nodeId: nguon.id, kind: 'source', label: nguon.label, status: khopSuKien ? 'ok' : 'skip',
    summary: lyDo, input: sample.payload, output: banGhi,
  })
  if (!khopSuKien) {
    return { id: genId('run'), workflowId: wf.id, workflowName: wf.name, sampleId: sample.id, sampleLabel: sample.label, startedAt: new Date().toISOString(), durationMs: 0, status: 'ok', steps, context: ctx }
  }

  const keTiep = (id: string, nhanh?: 'true' | 'false'): FlowNodeV2 | undefined => {
    const e = wf.edges.find((x) => x.source === id && (nhanh ? x.label === nhanh : !x.label || x.label === 'true'))
    return e ? wf.nodes.find((n) => n.id === e.target) : undefined
  }

  let cur = keTiep(nguon.id)
  let tongCho = 0
  let guard = 0
  while (cur && guard++ < 50) {
    const n = cur
    let nhanh: 'true' | 'false' | undefined
    try {
      if (n.kind === 'mapping') {
        const m = mappings.find((x) => x.id === n.config.mappingId) ?? mappings.find((x) => x.source === sample.source)
        const rec = m ? apAnhXa(banGhi, m.rules) : {}
        Object.assign(ctx, rec)
        steps.push({ nodeId: n.id, kind: 'mapping', label: n.label, status: m ? 'ok' : 'fail', summary: m ? `Áp "${m.name}": ${m.rules.length} luật` : 'Không tìm thấy bảng ánh xạ', input: banGhi, output: rec })
        if (!m) { status = 'fail'; break }
      } else if (n.kind === 'condition') {
        const rules = (n.config.rules as ConditionRuleV2[]) ?? []
        const logic = (n.config.logic as 'and' | 'or') ?? 'and'
        const kq = rules.map((r) => ({ r, ...kiemTraLuat(ctx, r) }))
        const dat = rules.length === 0 ? true : logic === 'and' ? kq.every((x) => x.dat) : kq.some((x) => x.dat)
        nhanh = dat ? 'true' : 'false'
        steps.push({
          nodeId: n.id, kind: 'condition', label: n.label, status: 'ok', branch: nhanh,
          summary: `${dat ? 'ĐÚNG' : 'SAI'} — ${kq.map((x) => `${x.r.field} ${x.r.op} "${x.r.value}" → ${x.dat ? '✓' : '✗'}`).join(logic === 'and' ? ' VÀ ' : ' HOẶC ')}`,
          input: Object.fromEntries(kq.map((x) => [x.r.field, x.giaTri])), output: { dat },
        })
      } else if (n.kind === 'delay') {
        const v = Number(n.config.value ?? 0); const u = String(n.config.unit ?? 'hours')
        tongCho += v * (MS_DON_VI[u] ?? 0)
        steps.push({ nodeId: n.id, kind: 'delay', label: n.label, status: 'wait', summary: `Chờ ${v} ${DON_VI_CHO[u] ?? u} (chạy thử: tua nhanh)`, output: { hen: `+${v} ${u}` } })
      } else if (n.kind === 'action') {
        const type = String(n.config.type ?? '')
        const out = thucHienHanhDong(type, n.config, ctx)
        steps.push({ nodeId: n.id, kind: 'action', label: n.label, status: out.ok ? 'ok' : 'fail', summary: out.tomTat, input: out.dauVao, output: out.dauRa })
        if (!out.ok) { status = 'fail'; break }
      }
    } catch (err) {
      steps.push({ nodeId: n.id, kind: n.kind, label: n.label, status: 'fail', summary: `Lỗi: ${(err as Error).message}` })
      status = 'fail'; break
    }
    cur = keTiep(n.id, nhanh)
  }

  return { id: genId('run'), workflowId: wf.id, workflowName: wf.name, sampleId: sample.id, sampleLabel: sample.label, startedAt: new Date().toISOString(), durationMs: tongCho, status, steps, context: ctx }
}

function thucHienHanhDong(type: string, cfg: Record<string, unknown>, ctx: Record<string, unknown>): { ok: boolean; tomTat: string; dauVao?: unknown; dauRa?: unknown } {
  const ten = ACTION_V2_LABELS[type as keyof typeof ACTION_V2_LABELS] ?? type
  const phone = layGiaTri(ctx, 'customer.phone')
  const khach = layGiaTri(ctx, 'customer.name') ?? '(chưa ghép khách)'
  switch (type) {
    case 'add_tag': case 'remove_tag': {
      const tag = thayBien(String(cfg.tag ?? ''), ctx)
      if (!tag) return { ok: false, tomTat: `${ten}: chưa nhập nhãn` }
      const tags = new Set<string>((layGiaTri(ctx, 'customer.tags') as string[]) ?? [])
      if (type === 'add_tag') tags.add(tag); else tags.delete(tag)
      datGiaTri(ctx, 'customer.tags', [...tags])
      return { ok: true, tomTat: `${type === 'add_tag' ? 'Gắn' : 'Gỡ'} nhãn "${tag}" cho ${khach} (${phone ?? 'không SĐT'})`, dauRa: { tag, tags: [...tags] } }
    }
    case 'send_message': case 'send_zalo_personal': case 'send_zalo_oa': case 'send_survey': {
      const noiDung = thayBien(String(cfg.message ?? ''), ctx)
      if (!phone) return { ok: false, tomTat: `${ten}: không có SĐT khách để ghép Zalo — nên rẽ sang "Tạo việc cho nhân viên"` }
      const kenh = type === 'send_zalo_personal' ? 'zalo_personal' : type === 'send_zalo_oa' ? 'zalo_oa' : String(cfg.channel || 'tự chọn')
      const link = type === 'send_survey' ? thayBien(String(cfg.formUrl ?? 'https://forms.gle/xxxx'), ctx) : undefined
      return { ok: true, tomTat: `Gửi ${type === 'send_survey' ? 'khảo sát' : 'tin'} qua ${kenh} tới ${khach} · ${phone}`, dauVao: { mau: cfg.message }, dauRa: { kenh, den: phone, noiDung: link ? `${noiDung}\n${link}` : noiDung } }
    }
    case 'send_zalo_zns': {
      const tpl = String(cfg.templateId ?? '')
      if (!tpl) return { ok: false, tomTat: 'ZNS: chưa nhập templateId' }
      if (!phone) return { ok: false, tomTat: 'ZNS: không có SĐT khách' }
      const params = Object.fromEntries(String(cfg.params ?? '').split('\n').filter((l) => l.includes('=')).map((l) => { const [k, v] = l.split('='); return [k.trim(), thayBien(v.trim(), ctx)] }))
      return { ok: true, tomTat: `Gửi ZNS mẫu ${tpl} tới ${phone}`, dauRa: { templateId: tpl, phone, params } }
    }
    case 'create_voucher': {
      const code = `${String(cfg.prefix ?? 'MVS')}${Math.random().toString(36).slice(2, 7).toUpperCase()}`
      const v = { code, giaTri: Number(cfg.value ?? 0), loai: String(cfg.kind ?? 'percent'), hetHan: `+${cfg.expireDays ?? 30} ngày`, ghiChu: thayBien(String(cfg.note ?? ''), ctx) }
      datGiaTri(ctx, 'voucher', v)
      return { ok: true, tomTat: `Tạo voucher ${code} (${v.giaTri}${v.loai === 'percent' ? '%' : 'đ'}, hạn ${v.hetHan}) → biến {{voucher.code}}`, dauRa: v }
    }
    case 'create_task': {
      const tieuDe = thayBien(String(cfg.title ?? 'Việc từ tự động hoá'), ctx)
      const nguoiNhan = thayBien(String(cfg.assignee || 'Nhóm CSKH'), ctx)
      return { ok: true, tomTat: `Tạo việc "${tieuDe}" cho ${nguoiNhan}`, dauRa: { tieuDe, nguoiNhan, hanXuLy: `+${cfg.dueHours ?? 24} giờ` } }
    }
    case 'update_property': {
      const key = String(cfg.key ?? ''); const val = thayBien(String(cfg.value ?? ''), ctx)
      if (!key) return { ok: false, tomTat: 'Cập nhật thuộc tính: chưa nhập tên thuộc tính' }
      datGiaTri(ctx, `customer.props.${key}`, val)
      return { ok: true, tomTat: `Đặt ${key} = "${val}" cho ${khach}`, dauRa: { [key]: val } }
    }
    case 'write_sheet': {
      const cols = String(cfg.columns ?? '').split(',').map((c) => c.trim()).filter(Boolean)
      const row = cols.map((c) => thayBien(c.startsWith('{{') ? c : `{{${c}}}`, ctx))
      return { ok: true, tomTat: `Ghi 1 dòng vào Sheet "${cfg.sheet || 'Sheet1'}" (${row.length} cột)`, dauRa: { sheet: cfg.sheet, row } }
    }
    case 'http_request': {
      const url = thayBien(String(cfg.url ?? ''), ctx)
      if (!/^https?:\/\//.test(url)) return { ok: false, tomTat: 'HTTP: URL không hợp lệ' }
      return { ok: true, tomTat: `${String(cfg.method ?? 'POST')} ${url} (chạy thử: không gọi thật)`, dauRa: { method: cfg.method ?? 'POST', url, body: thayBien(String(cfg.body ?? ''), ctx) } }
    }
    case 'send_notification': {
      const msg = thayBien(String(cfg.message ?? ''), ctx)
      return { ok: true, tomTat: `Thông báo nội bộ: ${msg.slice(0, 80)}`, dauRa: { message: msg } }
    }
    case 'send_friend_request': {
      const truong = String(cfg.phoneField ?? 'customer.phone')
      const so = layGiaTri(ctx, truong)
      if (!so) return { ok: false, tomTat: `${ten}: không có số điện thoại ở ${truong}` }
      const msg = thayBien(String(cfg.message ?? ''), ctx)
      return { ok: true, tomTat: `Gửi lời mời kết bạn tới ${so}${msg ? ` — "${msg.slice(0, 60)}"` : ''}`, dauVao: { phone: so }, dauRa: { phone: so, message: msg } }
    }
    default:
      return { ok: false, tomTat: `Hành động chưa hỗ trợ: ${type}` }
  }
}
